import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Loader2,
  MapPinned,
  Pencil,
  Plus,
  RotateCcw,
  Search,
  Trash2,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Skeleton } from '../../../components/ui/skeleton';
import { Switch } from '../../../components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { useApi } from '../../../hooks/useApi';
import { cn } from '../../../lib/utils';
import {
  deleteCustomerGeofence,
  extractList,
  fetchCustomerGeofences,
  formatRadius,
  normalizeGeofence,
  updateCustomerGeofence,
  buildGeofencePayload,
} from '../../../utils/geofence';

export default function Geofences() {
  const { get, put, patch, delete: del } = useApi();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const deviceIdFilter = searchParams.get('deviceId') || '';
  const vehicleIdFilter = searchParams.get('vehicleId') || '';

  const [rows, setRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [deletingIds, setDeletingIds] = useState(() => new Set());
  const [togglingIds, setTogglingIds] = useState(() => new Set());

  const loadGeofences = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data } = await fetchCustomerGeofences(get, {
        vehicleId: vehicleIdFilter || undefined,
        deviceId: deviceIdFilter || undefined,
      });
      const list = extractList(data).map(normalizeGeofence).filter((row) => row.id);
      setRows(list);
    } catch (error) {
      toast.error(error?.message || 'Unable to load geofences.');
      setRows([]);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [get, vehicleIdFilter, deviceIdFilter]);

  useEffect(() => {
    loadGeofences();
  }, [loadGeofences]);

  const handleRefresh = () => {
    setIsRefreshing(true);
    loadGeofences();
  };

  const filteredRows = useMemo(() => {
    let list = rows;
    if (deviceIdFilter) {
      list = list.filter(
        (row) => !row.deviceId || String(row.deviceId) === String(deviceIdFilter)
      );
    }
    if (vehicleIdFilter) {
      list = list.filter(
        (row) => !row.vehicleId || String(row.vehicleId) === String(vehicleIdFilter)
      );
    }
    if (!searchTerm.trim()) return list;
    const term = searchTerm.trim().toLowerCase();
    return list.filter(
      (row) =>
        row.name.toLowerCase().includes(term) ||
        row.vehicleName.toLowerCase().includes(term) ||
        row.vehicleNumber.toLowerCase().includes(term)
    );
  }, [rows, searchTerm, deviceIdFilter, vehicleIdFilter]);

  const handleDelete = async (row) => {
    if (!row?.id) return;
    if (!window.confirm(`Delete geofence "${row.name}"?`)) return;

    setDeletingIds((prev) => new Set(prev).add(row.id));
    try {
      await deleteCustomerGeofence(del, row.id, { vehicleId: row.vehicleId });
      setRows((prev) => prev.filter((item) => item.id !== row.id));
      toast.success('Geofence deleted.');
    } catch (error) {
      toast.error(error?.message || 'Failed to delete geofence.');
    } finally {
      setDeletingIds((prev) => {
        const next = new Set(prev);
        next.delete(row.id);
        return next;
      });
    }
  };

  const handleToggleEnabled = async (row, enabled) => {
    if (!row?.id) return;
    setTogglingIds((prev) => new Set(prev).add(row.id));
    const payload = buildGeofencePayload({
      name: row.name,
      vehicleId: row.vehicleId,
      deviceId: row.deviceId,
      centerLat: row.centerLat,
      centerLng: row.centerLng,
      radiusMeters: row.radiusMeters,
      alertOnEnter: row.alertOnEnter,
      alertOnExit: row.alertOnExit,
      enabled,
    });

    try {
      await updateCustomerGeofence(put, patch, row.id, payload);
      setRows((prev) =>
        prev.map((item) => (item.id === row.id ? { ...item, enabled } : item))
      );
      toast.success(enabled ? 'Geofence enabled.' : 'Geofence disabled.');
    } catch (error) {
      toast.error(error?.message || 'Failed to update geofence.');
    } finally {
      setTogglingIds((prev) => {
        const next = new Set(prev);
        next.delete(row.id);
        return next;
      });
    }
  };

  const addHref = useMemo(() => {
    const params = new URLSearchParams();
    if (vehicleIdFilter) params.set('vehicleId', vehicleIdFilter);
    if (deviceIdFilter) params.set('deviceId', deviceIdFilter);
    const qs = params.toString();
    return `/customer/geofences/add${qs ? `?${qs}` : ''}`;
  }, [vehicleIdFilter, deviceIdFilter]);

  const showEmptyState = !isLoading && filteredRows.length === 0;

  const panelClass =
    'overflow-hidden rounded-xl border border-transparent bg-card text-card-foreground shadow-sm dark:border-zinc-800/80 dark:bg-[#18181b]';
  const mobileShellClass = 'block md:hidden space-y-2.5';
  const panelToolbarClass = 'flex items-center gap-2 px-0 py-2.5 sm:px-4';
  const searchWrapClass = 'relative min-w-0 flex-1';
  const searchInputClass =
    'h-10 w-full rounded-full border-0 bg-muted pl-10 pr-4 text-[13px] text-foreground shadow-none placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:ring-offset-0 dark:bg-[#27272a] dark:text-zinc-100 dark:placeholder:text-zinc-500';
  const toolbarButtonClass =
    'h-10 w-10 shrink-0 rounded-full border-0 bg-muted p-0 text-zinc-600 hover:bg-muted/80 dark:bg-[#27272a] dark:text-zinc-300 dark:hover:bg-[#2f2f33] dark:hover:text-zinc-100';
  const addButtonClass =
    'h-10 shrink-0 rounded-full border-0 bg-sky-600 px-4 text-[13px] font-medium text-white hover:bg-sky-500 dark:bg-sky-500 dark:text-zinc-950 dark:hover:bg-sky-400';
  const gridHeaderRowClass = 'border-0 hover:bg-transparent';
  const gridHeadClass =
    'h-9 px-3 py-0 text-[12px] font-medium text-muted-foreground select-none whitespace-nowrap dark:text-zinc-500';
  const gridCellClass =
    'px-3 py-1.5 text-[12px] leading-tight text-foreground align-middle whitespace-nowrap dark:text-zinc-200';
  const gridRowBaseClass =
    'h-10 border-0 transition-colors hover:bg-muted/50 dark:hover:bg-[#27272a]';
  const getGridRowClass = (index) =>
    cn(
      gridRowBaseClass,
      index % 2 === 1
        ? 'bg-muted/40 dark:bg-[#1f1f23]'
        : 'bg-background/50 dark:bg-[#18181b]'
    );
  const compactTableClass =
    'min-w-full text-xs [&_td]:p-0 [&_th]:p-0 [&_tr]:h-10 [&_tr]:border-0';
  const mobileCardClass =
    'overflow-hidden rounded-xl border border-border/50 bg-card p-3.5 shadow-sm dark:border-zinc-800/80 dark:bg-[#1f1f23]';
  const mobileCardInsetClass =
    'rounded-lg bg-muted/70 px-2.5 py-2 dark:bg-[#27272a]';
  const iconButtonClass =
    'h-8 w-8 rounded-lg text-zinc-600 hover:bg-muted dark:text-zinc-400 dark:hover:bg-[#323238] dark:hover:text-zinc-100';
  const deleteButtonClass =
    'h-8 w-8 rounded-lg text-red-600 hover:bg-red-500/10 dark:text-red-400 dark:hover:bg-red-500/15 dark:hover:text-red-300';
  const chipClass =
    'inline-flex items-center rounded-md bg-sky-500/10 px-1.5 py-0.5 text-[10px] font-medium text-sky-700 dark:bg-sky-400/15 dark:text-sky-300';
  const mutedChipClass =
    'inline-flex items-center rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground dark:bg-[#323238] dark:text-zinc-400';

  const renderAlertChips = (row) => {
    const chips = [];
    if (row.alertOnEnter) {
      chips.push(
        <span key="enter" className={chipClass}>
          Enter
        </span>
      );
    }
    if (row.alertOnExit) {
      chips.push(
        <span
          key="exit"
          className="inline-flex items-center rounded-md bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:bg-amber-400/15 dark:text-amber-300"
        >
          Exit
        </span>
      );
    }
    if (chips.length === 0) {
      return <span className={mutedChipClass}>None</span>;
    }
    return <div className="flex flex-wrap items-center gap-1">{chips}</div>;
  };

  const renderToolbar = () => (
    <div className={panelToolbarClass}>
      <div className={searchWrapClass}>
        <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400 dark:text-zinc-500" />
        <Input
          placeholder="Search zones..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={searchInputClass}
        />
      </div>
      <Button
        variant="ghost"
        size="icon"
        onClick={handleRefresh}
        className={toolbarButtonClass}
        disabled={isRefreshing}
        aria-label="Refresh geofences"
      >
        <RotateCcw
          className={cn('h-4 w-4', isRefreshing && 'animate-spin')}
        />
      </Button>
      <Button asChild className={addButtonClass}>
        <Link to={addHref}>
          <Plus className="mr-1.5 h-4 w-4" />
          Add zone
        </Link>
      </Button>
    </div>
  );

  const MobileCard = ({ row }) => {
    const isDeleting = deletingIds.has(row.id);
    const isToggling = togglingIds.has(row.id);
    return (
      <article className={mobileCardClass}>
        <div className="mb-3 flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
              Zone
            </p>
            <p className="truncate text-[13px] font-medium text-zinc-900 dark:text-zinc-100">
              {row.name}
            </p>
            <p className="truncate text-[11px] text-zinc-600 dark:text-zinc-400">
              {row.vehicleName || row.vehicleNumber || 'All vehicles'}
              {row.vehicleNumber && row.vehicleName ? ` · ${row.vehicleNumber}` : ''}
            </p>
          </div>
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-sky-500/10 dark:bg-sky-400/15">
            <MapPinned className="h-4 w-4 text-sky-600 dark:text-sky-400" />
          </div>
        </div>

        <div className={`mb-3 flex flex-wrap items-center gap-2 ${mobileCardInsetClass}`}>
          <span className={chipClass}>Radius {formatRadius(row.radiusMeters)}</span>
          {renderAlertChips(row)}
        </div>

        <div className={`flex items-center justify-between gap-2 ${mobileCardInsetClass}`}>
          <div className="flex items-center gap-2">
            <Switch
              checked={row.enabled}
              disabled={isToggling}
              onCheckedChange={(checked) => handleToggleEnabled(row, checked)}
              aria-label={`Enable ${row.name}`}
              className="data-[state=checked]:bg-sky-600 dark:data-[state=checked]:bg-sky-500 data-[state=unchecked]:bg-zinc-300 dark:data-[state=unchecked]:bg-zinc-600"
            />
            <span className="text-[11px] text-zinc-600 dark:text-zinc-400">
              {row.enabled ? 'On' : 'Off'}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className={iconButtonClass}
              onClick={() => navigate(`/customer/geofences/${row.id}/edit`)}
              aria-label={`Edit ${row.name}`}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className={deleteButtonClass}
              disabled={isDeleting}
              onClick={() => handleDelete(row)}
              aria-label={`Delete ${row.name}`}
            >
              {isDeleting ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Trash2 className="h-3.5 w-3.5" />
              )}
            </Button>
          </div>
        </div>
      </article>
    );
  };

  return (
    <div className="relative w-full max-w-full overflow-hidden">
      <div className="mb-3">
        <h1 className="text-lg font-bold text-zinc-900 sm:text-xl dark:text-zinc-100">
          Geofences
        </h1>
        <p className="mt-0.5 text-[12px] text-zinc-600 dark:text-zinc-400">
          Create safe zones and get alerts when a vehicle enters or leaves.
        </p>
      </div>

      <div className={mobileShellClass}>
        {renderToolbar()}
        <div className="space-y-2.5">
          {isLoading ? (
            Array.from({ length: 3 }).map((_, index) => (
              <div key={`m-skel-${index}`} className={mobileCardClass}>
                <Skeleton className="mb-3 h-4 w-36 dark:bg-zinc-700" />
                <Skeleton className="mb-3 h-8 rounded-lg dark:bg-zinc-700" />
                <Skeleton className="h-10 rounded-lg dark:bg-zinc-700" />
              </div>
            ))
          ) : showEmptyState ? (
            <div className={`${mobileCardClass} py-12 text-center`}>
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-sky-500/10 dark:bg-sky-400/10">
                <MapPinned className="h-6 w-6 text-sky-600 dark:text-sky-400" />
              </div>
              <h3 className="mb-2 text-[15px] font-medium text-zinc-900 dark:text-zinc-100">
                No geofences yet
              </h3>
              <p className="mb-4 text-[12px] text-zinc-500 dark:text-zinc-400">
                {searchTerm
                  ? 'Try adjusting your search.'
                  : 'Add a circular safe zone on the map to get started.'}
              </p>
              {!searchTerm ? (
                <Button asChild size="sm" className={addButtonClass}>
                  <Link to={addHref}>
                    <Plus className="mr-1.5 h-4 w-4" />
                    Add zone
                  </Link>
                </Button>
              ) : null}
            </div>
          ) : (
            filteredRows.map((row) => <MobileCard key={row.id} row={row} />)
          )}
        </div>
      </div>

      <div className={`hidden md:block ${panelClass}`}>
        <div className="border-b border-transparent px-3 py-2.5 sm:px-4 dark:border-zinc-800/80">
          {renderToolbar()}
        </div>
        <div className="overflow-x-auto">
          <Table className={compactTableClass}>
            <TableHeader className="[&_tr]:border-0">
              <TableRow className={gridHeaderRowClass}>
                <TableHead className={`${gridHeadClass} min-w-[140px]`}>Zone</TableHead>
                <TableHead className={`${gridHeadClass} min-w-[140px]`}>Vehicle</TableHead>
                <TableHead className={`${gridHeadClass} min-w-[90px]`}>Radius</TableHead>
                <TableHead className={`${gridHeadClass} min-w-[110px]`}>Alerts</TableHead>
                <TableHead className={`${gridHeadClass} min-w-[90px]`}>Status</TableHead>
                <TableHead className={`${gridHeadClass} min-w-[100px]`}>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={`skel-${index}`} className={getGridRowClass(index)}>
                    {Array.from({ length: 6 }).map((__, cell) => (
                      <TableCell key={cell} className={gridCellClass}>
                        <Skeleton className="h-3.5 w-20 dark:bg-zinc-700" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : showEmptyState ? (
                <TableRow>
                  <TableCell colSpan={6} className="py-12 text-center">
                    <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-sky-500/10 dark:bg-sky-400/10">
                      <MapPinned className="h-6 w-6 text-sky-600 dark:text-sky-400" />
                    </div>
                    <h3 className="mb-2 text-[15px] font-medium text-zinc-900 dark:text-zinc-100">
                      No geofences yet
                    </h3>
                    <p className="mb-4 text-[13px] text-zinc-500 dark:text-zinc-400">
                      {searchTerm
                        ? 'Try adjusting your search.'
                        : 'Add a circular safe zone on the map to get started.'}
                    </p>
                    {!searchTerm ? (
                      <Button asChild size="sm" className={addButtonClass}>
                        <Link to={addHref}>
                          <Plus className="mr-1.5 h-4 w-4" />
                          Add zone
                        </Link>
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ) : (
                filteredRows.map((row, index) => {
                  const isDeleting = deletingIds.has(row.id);
                  const isToggling = togglingIds.has(row.id);

                  return (
                    <TableRow key={row.id} className={getGridRowClass(index)}>
                      <TableCell className={gridCellClass}>
                        <p className="truncate font-medium text-zinc-900 dark:text-zinc-100">
                          {row.name}
                        </p>
                      </TableCell>
                      <TableCell className={gridCellClass}>
                        <div className="max-w-[160px]">
                          <p className="truncate text-zinc-800 dark:text-zinc-200">
                            {row.vehicleName || row.vehicleNumber || '—'}
                          </p>
                          {row.vehicleNumber && row.vehicleName ? (
                            <p className="truncate text-[11px] text-zinc-500 dark:text-zinc-500">
                              {row.vehicleNumber}
                            </p>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell className={gridCellClass}>
                        <span className={chipClass}>{formatRadius(row.radiusMeters)}</span>
                      </TableCell>
                      <TableCell className={gridCellClass}>{renderAlertChips(row)}</TableCell>
                      <TableCell className={gridCellClass}>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={row.enabled}
                            disabled={isToggling}
                            onCheckedChange={(checked) => handleToggleEnabled(row, checked)}
                            aria-label={`Enable ${row.name}`}
                            className="data-[state=checked]:bg-sky-600 dark:data-[state=checked]:bg-sky-500 data-[state=unchecked]:bg-zinc-300 dark:data-[state=unchecked]:bg-zinc-600"
                          />
                          <span className="text-[11px] text-zinc-600 dark:text-zinc-400">
                            {row.enabled ? 'On' : 'Off'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className={gridCellClass}>
                        <div className="flex items-center gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className={iconButtonClass}
                            onClick={() => navigate(`/customer/geofences/${row.id}/edit`)}
                            aria-label={`Edit ${row.name}`}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className={deleteButtonClass}
                            disabled={isDeleting}
                            onClick={() => handleDelete(row)}
                            aria-label={`Delete ${row.name}`}
                          >
                            {isDeleting ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Trash2 className="h-3.5 w-3.5" />
                            )}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
